<script module lang="ts">
  import type {
    OrganizationUser,
    AdminUserListItem,
    OrganizationFrame,
  } from "@kontextwerk/control-web-client"
  import type {
    PermissionDefinition,
    PermissionState,
  } from "@kontextwerk/control-web-frame/logic/permissions/model"
  import type { PermissionSectionConfig } from "../../_shared/_logic/permission-sections"
  import type { UserViewFrameContext } from "./_shared/_logic/user-types"
  export type { UserViewFrameContext } from "./_shared/_logic/user-types"
</script>

<script lang="ts">
  import PageTemplate from "@kontextwerk/web-sdk/ui/layout/PageTemplate.svelte"
  import type { RecordDetailConfig } from "@kontextwerk/web-sdk/ui/layout/recordDetail/types"
  import { buildUserHeader, userRecordIcon } from "./_logic/header"
  import { UserViewFrameWrapper } from "@kontextwerk/control-web-frame"
  import {
    ActivityContextPanel,
    composeControlActivityCatalog,
    frameConfig as activityFrameConfig,
  } from "@kontextwerk/control-web-frame"
  import GeneralPage from "@kontextwerk/control-web-frame/routes/OrgaView/subviews/User/pages/General/GeneralPage.svelte"
  import PermissionsPage from "./pages/permissions"
  import IntegrationsPage from "./pages/integrations"
  import WorkingHoursPage from "./pages/working-hours"
  import {
    PERMISSION_DEFINITIONS,
    PERMISSION_SECTIONS,
  } from "../../_shared/_logic/permission-sections"
  import { location } from "../../../../_shared/_logic/store"
  import { getAdminApi } from "../../../../api/kehrblatt/client"
  import { fetchUserSettings } from "../../../../api/kehrblatt/user-settings"
  import {
    buildAppPermissionState,
    resolveAllowedEmployeeIds,
  } from "../../../_shared/_logic/permissions-model"
  import { buildUserViewNavigation, buildUserViewPaths } from "./navigation"
  import { getKehrblattControlAuthContext } from "../../../_shared/_logic/auth-context"
  import { userDisplayName } from "@kontextwerk/control-web-frame/logic/userDisplay"

  const {
    userId = null,
    organizationId = null,
    organizationSlug = null,
    organizationResolvedId = null,
  } = $props<{
    userId?: string | null
    organizationId?: string | null
    organizationSlug?: string | null
    organizationResolvedId?: string | null
  }>()

  let frameConfig = $state<any>(null)
  const activityCatalog = $derived(
    composeControlActivityCatalog($activityFrameConfig?.activity?.catalog),
  )
  const authContext = getKehrblattControlAuthContext()
  const emptyPermissions = buildAppPermissionState(null)
  const mergeUserSettings = async (
    user: OrganizationUser | null | undefined,
  ): Promise<OrganizationUser | null> => {
    if (!user?.id) return user ?? null

    try {
      const settings = await fetchUserSettings(user.id)

      return {
        ...user,
        ...settings,
      }
    } catch (err) {
      console.error("user_settings_load_failed", err)

      return user
    }
  }

  const userViewLoaders = {
    fetchOrganization: (id: string): Promise<OrganizationFrame> =>
      getAdminApi().organization.registry.getFrame(id),
    listOrgUsers: async (
      orgId: string,
      params: { query?: string | null; limit?: number },
    ): Promise<{ items?: OrganizationUser[] }> => {
      const response = await getAdminApi().users.listForOrg(orgId, params)
      const items = await Promise.all(
        (response.items ?? []).map((entry) => mergeUserSettings(entry)),
      )

      return { ...response, items: items.filter(Boolean) as OrganizationUser[] }
    },
    listAdminUsers: async (params: {
      query?: string | null
      limit?: number
    }): Promise<{ items?: AdminUserListItem[] }> => {
      const response = await getAdminApi().users.list(params)
      const items = await Promise.all(
        (response.items ?? []).map(async (entry) => {
          const enriched = await mergeUserSettings(entry as OrganizationUser)

          return { ...entry, ...(enriched ?? {}) }
        }),
      )

      return { ...response, items: items as AdminUserListItem[] }
    },
    listOrganizations: () => getAdminApi().organization.registry.list(),
  }

  const user = $derived(frameConfig?.user ?? null)
  const organizationIdForUser = $derived(
    frameConfig?.orgContext?.id ?? frameConfig?.resolvedOrganizationId ?? null,
  )
  const administrationPath = $derived.by(() => {
    if (!organizationId && !organizationSlug) return null

    const segment = frameConfig?.orgContext?.slug ?? organizationSlug ?? organizationId

    return segment ? `/organizations/${encodeURIComponent(segment)}/administration` : null
  })
  const usersListPath = $derived(
    administrationPath ? `${administrationPath}/users` : (frameConfig?.usersListPath ?? null),
  )
  const tabPaths = $derived.by(() => buildUserViewPaths(frameConfig?.tabPath))

  const headerTitle = $derived(userDisplayName(user) || "Benutzer")
  const headerSubtitle = $derived(user?.email ?? frameConfig?.orgContext?.name ?? "Benutzerdetails")
  const loading = $derived(frameConfig?.loading ?? false)
  const error = $derived(frameConfig?.error ?? null)
  const permissions = $derived($authContext?.permissions ?? emptyPermissions)
  const resolvedTargetUserId = $derived(
    user?.id && (!userId || user.id === userId) ? user.id : null,
  )
  // Route identity; without a user id the profile belongs to the signed-in user.
  const routeRecordKey = $derived(
    `user:${organizationSlug ?? organizationId ?? "global"}:${userId ?? "self"}`,
  )
  const confirmedUser = $derived(resolvedTargetUserId && !error ? user : null)
  const recordDetail = $derived<RecordDetailConfig>({
    key: routeRecordKey,
    loading: !confirmedUser && (frameConfig?.loading ?? true),
    header: confirmedUser
      ? buildUserHeader(confirmedUser, frameConfig?.orgContext ?? null)
      : { icon: userRecordIcon, fields: [] },
  })
  const schedulingViewEmployeeIds = $derived(
    resolveAllowedEmployeeIds(
      permissions,
      "termin.scheduling",
      $authContext?.currentUserId,
      "view",
    ),
  )
  const schedulingManageEmployeeIds = $derived(
    resolveAllowedEmployeeIds(
      permissions,
      "termin.scheduling",
      $authContext?.currentUserId,
      "manage",
    ),
  )
  const scopeAllowsTargetUser = (employeeIds: string[] | null, targetUserId: string | null) =>
    Boolean(targetUserId && (employeeIds == null || employeeIds.includes(targetUserId)))
  const canViewCalendarIntegration = $derived(
    scopeAllowsTargetUser(schedulingViewEmployeeIds, resolvedTargetUserId),
  )
  const canManageCalendarIntegration = $derived(
    scopeAllowsTargetUser(schedulingManageEmployeeIds, resolvedTargetUserId),
  )
  const canDisconnectOwnCalendarIntegration = $derived(
    Boolean(resolvedTargetUserId && resolvedTargetUserId === ($authContext?.currentUserId ?? null)),
  )
  // Tab content deliberately remounts once the target user resolves; the record frame does not.
  const viewKey = $derived(
    `user:${organizationSlug ?? organizationId ?? "global"}:` +
      (resolvedTargetUserId ? `resolved:${resolvedTargetUserId}` : `loading:${userId ?? "none"}`),
  )

  const retryLoad = () => {
    frameConfig?.reload()
  }

  const handleUserUpdated = (updated: OrganizationUser) => {
    frameConfig?.setUser(updated)
  }

  const viewFrameContext = $derived.by<UserViewFrameContext>(() => ({
    user,
    currentUserId: $authContext?.currentUserId ?? null,
    organizationId: organizationIdForUser,
    loading,
    error,
    onRetry: retryLoad,
    onUpdated: handleUserUpdated,
    permissions,
    canViewCalendarIntegration,
    canManageCalendarIntegration,
    canDisconnectOwnCalendarIntegration,
    permissionSections: PERMISSION_SECTIONS,
    permissionDefinitions: PERMISSION_DEFINITIONS,
  }))

  const pageNavigation = $derived.by(() =>
    buildUserViewNavigation({
      context: viewFrameContext,
      paths: tabPaths,
      snippets: {
        general: generalPageContent,
        permissions: permissionsPageContent,
        integrations: integrationsPageContent,
        workingHours: workingHoursPageContent,
      },
    }),
  )
</script>

{#snippet generalPageContent(activeTab: string)}
  <GeneralPage {viewFrameContext} {activeTab} />
{/snippet}

{#snippet permissionsPageContent(activeTab: string)}
  <PermissionsPage {viewFrameContext} {activeTab} />
{/snippet}

{#snippet integrationsPageContent(activeTab: string)}
  <IntegrationsPage {viewFrameContext} {activeTab} />
{/snippet}

{#snippet workingHoursPageContent(activeTab: string)}
  <WorkingHoursPage {viewFrameContext} {activeTab} />
{/snippet}

<UserViewFrameWrapper
  {userId}
  {organizationId}
  {organizationSlug}
  {organizationResolvedId}
  loaders={userViewLoaders}
  bind:config={frameConfig}
>
  {#snippet viewFrame(_config)}
    {#if user && organizationIdForUser && resolvedTargetUserId && !loading && !error}
      <ActivityContextPanel
        context={{
          organization_id: organizationIdForUser,
          object_type: "user",
          object_id: resolvedTargetUserId,
        }}
        label={headerTitle}
        operations={getAdminApi().activity}
        catalog={activityCatalog}
      />
    {/if}
    <PageTemplate
      {viewKey}
      {recordDetail}
      title={headerTitle}
      subtitle={headerSubtitle}
      {pageNavigation}
      appBreadcrumbs={administrationPath && usersListPath
        ? [
            { title: "Verwaltungsbereich", href: administrationPath },
            { title: "Benutzer", href: usersListPath },
            { title: headerTitle },
          ]
        : undefined}
      locationStore={location}
      gobackLink={usersListPath ?? ""}
    />
  {/snippet}
</UserViewFrameWrapper>
